/**
 * ヒーロー型ポスター
 *
 * AI が選んだ 1 枚を全面に敷き、下半分にコピーと数字を重ねる映画ポスター風。
 */

import { shortPrefectureName } from '@/lib/prefectures'

import { dateRangeLabel, durationLabel } from '../trip-utils'
import type { PosterInput } from './poster-canvas'
import {
  BG,
  drawCover,
  drawKeywordPills,
  drawRoundedCover,
  drawRoute,
  drawScrim,
  drawStats,
  fitLines,
  FONT,
  HEIGHT,
  MARGIN,
  statItems,
  SUBTLE,
  TEXT,
  WIDTH,
} from './poster-canvas'

export const renderHeroPoster = (ctx: CanvasRenderingContext2D, input: PosterInput) => {
  const { trip, accent, hero, collage, headline, subcopy, keywords, routePoints } = input

  ctx.fillStyle = BG
  ctx.fillRect(0, 0, WIDTH, HEIGHT)
  if (hero) drawCover(ctx, hero, 0, 0, WIDTH, HEIGHT)

  drawScrim(ctx, 0, 420, 0.75, 0)
  drawScrim(ctx, 560, HEIGHT, 0, 0.96)

  // ── 上部：期間と日付 ─────────────────────────
  ctx.fillStyle = accent
  ctx.font = FONT(28, 700)
  ctx.fillText(durationLabel(trip), MARGIN, MARGIN + 40)

  ctx.fillStyle = TEXT
  ctx.font = FONT(30, 700)
  ctx.fillText(trip.title, MARGIN, MARGIN + 86)

  ctx.fillStyle = SUBTLE
  ctx.font = FONT(24)
  ctx.fillText(dateRangeLabel(trip), MARGIN, MARGIN + 126)

  // ── 下部：ボトムアップで積む ───────────────────
  const statsTop = HEIGHT - MARGIN - 96
  drawStats(ctx, statItems(trip), statsTop)

  const keywordsBaseline = statsTop - 48
  if (keywords.length > 0) drawKeywordPills(ctx, keywords.slice(0, 3), MARGIN, keywordsBaseline, accent)

  const subcopyBaseline = keywordsBaseline - 62
  ctx.fillStyle = SUBTLE
  ctx.font = FONT(28)
  ctx.fillText(subcopy, MARGIN, subcopyBaseline)

  const maxWidth = WIDTH - MARGIN * 2
  const { size, lines } = fitLines(ctx, headline, maxWidth, 2, 92, 48)
  const lineHeight = size * 1.18
  let baseline = subcopyBaseline - 52
  for (const line of [...lines].reverse()) {
    ctx.fillStyle = TEXT
    ctx.font = FONT(size, 700)
    ctx.fillText(line, MARGIN, baseline)
    baseline -= lineHeight
  }

  // ── コラージュ：見出しの上に小さく並べる ───────────
  const thumbSize = 148
  const thumbTop = baseline - thumbSize + size * 0.2
  collage.slice(0, 3).forEach((bitmap, i) => {
    drawRoundedCover(ctx, bitmap, MARGIN + (thumbSize + 14) * i, thumbTop, thumbSize, thumbSize, 16)
  })

  // ── ルート：右上に小さく ────────────────────
  drawRoute(ctx, routePoints, { x: WIDTH - MARGIN - 240, y: MARGIN + 20, w: 240, h: 240 }, accent)

  const prefLabel = trip.stats.prefectures.map(shortPrefectureName).join(' · ')
  if (prefLabel) {
    ctx.textAlign = 'right'
    ctx.fillStyle = SUBTLE
    ctx.font = FONT(24)
    ctx.fillText(prefLabel, WIDTH - MARGIN, MARGIN + 300)
    ctx.textAlign = 'left'
  }
}
